/**
 * Editable model for an import draft. Same idea as the recipe editor: every
 * field is held as typed text and converted back to the `ImportDraft` shape
 * only when the reviewer saves or approves. Pure and unit-tested.
 */
import { parseQuantity } from '@fewerbags/core';
import type {
  ImportDraft,
  ImportDraftIngredient,
  ImportDraftStep,
  UnitKey,
} from '@fewerbags/schema';
import { newKey, quantityText } from '../recipes/recipe-form';

export interface DraftIngredientRow {
  key: string;
  ingredient: string;
  qtyText: string;
  unit: UnitKey;
  prep: string;
  optional: boolean;
  raw: string;
  confidence: ImportDraftIngredient['confidence'];
  notes: string[];
}

export interface DraftStepRow {
  key: string;
  text: string;
  durationText: string;
  attended: boolean;
  timer: boolean;
  canDoAhead: boolean;
  equipment: ImportDraftStep['equipment'];
}

export interface DraftState {
  title: string;
  description: string;
  servings: string;
  prepMin: string;
  cookMin: string;
  totalMin: string;
  imageUrl: string | null;
  tags: string[];
  ingredients: DraftIngredientRow[];
  steps: DraftStepRow[];
  source: ImportDraft['source'];
  reviewNotes: string[];
}

function text(value: number | null): string {
  return value === null ? '' : String(value);
}

function numberOrNull(value: string): number | null {
  const t = value.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function draftToState(draft: ImportDraft): DraftState {
  return {
    title: draft.title ?? '',
    description: draft.description ?? '',
    servings: text(draft.servings),
    prepMin: text(draft.prepMin),
    cookMin: text(draft.cookMin),
    totalMin: text(draft.totalMin),
    imageUrl: draft.imageUrl,
    tags: [...draft.tags],
    ingredients: draft.ingredients.map((line) => ({
      key: newKey('draft-ing'),
      ingredient: line.ingredient,
      qtyText: quantityText(line.qty, line.qtyMax),
      unit: line.unit,
      prep: line.prep ?? '',
      optional: line.optional,
      raw: line.raw,
      confidence: line.confidence,
      notes: line.notes,
    })),
    steps: draft.steps.map((step) => ({
      key: newKey('draft-step'),
      text: step.text,
      durationText: text(step.durationMin),
      attended: step.attended,
      timer: step.timer,
      canDoAhead: step.canDoAhead,
      equipment: step.equipment,
    })),
    source: draft.source,
    reviewNotes: draft.reviewNotes,
  };
}

export function stateToDraft(state: DraftState): ImportDraft {
  const servings = numberOrNull(state.servings);
  return {
    title: state.title.trim() === '' ? null : state.title.trim(),
    description: state.description.trim() === '' ? null : state.description.trim(),
    servings: servings === null ? null : Math.max(1, Math.round(servings)),
    prepMin: numberOrNull(state.prepMin),
    cookMin: numberOrNull(state.cookMin),
    totalMin: numberOrNull(state.totalMin),
    imageUrl: state.imageUrl,
    tags: state.tags,
    ingredients: state.ingredients
      .filter((row) => row.ingredient.trim() !== '' || row.raw.trim() !== '')
      .map((row) => {
        const q = parseQuantity(row.qtyText);
        return {
          ingredient: row.ingredient.trim(),
          qty: q.qty !== null && q.qty > 0 ? q.qty : null,
          qtyMax: q.qtyMax !== undefined && q.qtyMax > 0 ? q.qtyMax : null,
          unit: row.unit,
          prep: row.prep.trim() === '' ? null : row.prep.trim(),
          optional: row.optional,
          raw: row.raw,
          confidence: row.confidence,
          notes: row.notes,
        };
      }),
    steps: state.steps
      .filter((row) => row.text.trim() !== '')
      .map((row) => {
        const duration = numberOrNull(row.durationText);
        return {
          text: row.text.trim(),
          durationMin: duration !== null && duration > 0 ? duration : null,
          attended: row.attended,
          timer: row.timer,
          canDoAhead: row.canDoAhead,
          ...(row.equipment ? { equipment: row.equipment } : {}),
        };
      }),
    source: state.source,
    reviewNotes: state.reviewNotes,
  };
}

/** The same three checks the API makes on approve, so the button can say why it is off. */
export function approvalBlockers(state: DraftState): string[] {
  const draft = stateToDraft(state);
  const out: string[] = [];
  if (draft.title === null) out.push('Give the recipe a title.');
  if (draft.ingredients.length === 0) out.push('Keep at least one ingredient.');
  if (draft.steps.length === 0) out.push('Keep at least one step.');
  return out;
}

export type ImportTab = 'url' | 'text' | 'photo';

/** Import sources to offer; "photo" only when the server reports the feature. */
export function importTabs(features: { photoImport: boolean } | undefined): ImportTab[] {
  return features?.photoImport ? ['url', 'text', 'photo'] : ['url', 'text'];
}
